import React, { useState } from "react";

/*
  CourseDetailPage-LuxuryEditorial.js
  Third premium course-detail concept for a university website.

  React + Tailwind CSS only.
  No custom CSS.
  Brand colours:
  Navy      #071A2F
  Deep Navy #0C2744
  Gold      #D5AD57
  Dark Gold #A77B2D
  Ivory     #F7F2E8
  Burgundy  #8B1E3F
*/

const cx = (...v) => v.filter(Boolean).join(" ");

const Arrow = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path d="M7 17 17 7M9 7h8v8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Check = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Plus = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const Download = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <path d="M12 4v10m0 0 4-4m-4 4-4-4M5 19h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Eyebrow = ({ children, light = false }) => (
  <div className={cx("flex items-center gap-3 text-sm font-bold uppercase tracking-[0.15em]", light ? "text-[#D5AD57]" : "text-[#8B1E3F]")}>
    <span className="h-px w-10 bg-current" />
    {children}
  </div>
);

const sampleCourse = {
  school: "School of Information & Technology",
  title: "Bachelor of Computer Applications",
  shortTitle: "BCA",
  level: "Undergraduate",
  duration: "3 Years",
  mode: "Full Time",
  campus: "ISBM University",
  eligibility: "10+2 or equivalent from a recognised board, subject to University norms.",
  heroImage: "",
  summary:
    "A future-focused undergraduate programme combining computing foundations, software development, digital systems and practical problem-solving.",
  overview:
    "The Bachelor of Computer Applications programme is designed to develop foundational and applied capabilities across computing, programming, databases, web technologies, networks and software development. The learning experience combines academic study with laboratories, projects and professional development.",
  highlights: [
    ["Programming", "Develop structured computational thinking and coding foundations."],
    ["Web Technologies", "Explore contemporary web development concepts and applications."],
    ["Data & Databases", "Understand data organisation, databases and information systems."],
    ["Software Practice", "Apply concepts through laboratories, assignments and projects."],
  ],
  curriculum: [
    {
      label: "Foundation",
      year: "Year 01",
      description: "Establish the academic and technical foundations required for progressive computing study.",
      subjects: ["Computer Fundamentals", "Programming Fundamentals", "Mathematics", "Communication Skills", "Digital Systems"],
    },
    {
      label: "Development",
      year: "Year 02",
      description: "Move into application-oriented subjects across software, databases, networks and web technologies.",
      subjects: ["Data Structures", "Database Management Systems", "Computer Networks", "Web Technologies", "Software Engineering"],
    },
    {
      label: "Integration",
      year: "Year 03",
      description: "Integrate technical knowledge through advanced study, electives and project-based learning.",
      subjects: ["Advanced Programming", "Cloud Fundamentals", "Cybersecurity Basics", "Elective", "Major Project"],
    },
  ],
  methodology: [
    ["Academic Foundation", "Structured classroom learning develops strong conceptual understanding."],
    ["Laboratory Practice", "Practical sessions translate computing concepts into hands-on experience."],
    ["Project Work", "Projects encourage application, teamwork, documentation and presentation."],
    ["Professional Readiness", "Communication and career-oriented activities support workplace preparation."],
  ],
  outcomes: [
    "Apply foundational computing concepts to structured problems.",
    "Develop basic software and web-based solutions.",
    "Work with databases, networks and contemporary digital systems.",
    "Communicate technical ideas in academic and collaborative settings.",
    "Build readiness for entry-level technology roles and higher studies.",
  ],
  careers: [
    "Software Developer",
    "Web Developer",
    "Application Support",
    "Database Support",
    "Junior Data Analyst",
    "IT Support Professional",
    "QA Associate",
    "Higher Studies",
  ],
  admissions: [
    ["01", "Check Eligibility", "Review the current programme eligibility and admission guidelines."],
    ["02", "Submit Application", "Complete the University's online admission application."],
    ["03", "Document Review", "Submit applicable academic and identity documents for verification."],
    ["04", "Counselling", "Connect with the admissions team for programme and process guidance."],
    ["05", "Admission Confirmation", "Complete applicable formalities and fee payment."],
  ],
  faq: [
    ["Who can apply for this programme?", "Applicants should have completed 10+2 or an equivalent examination from a recognised board, subject to current University eligibility norms."],
    ["Does the programme include practical learning?", "Yes. The academic structure may include laboratories, assignments, projects, presentations and practical learning activities."],
    ["What can I pursue after BCA?", "Graduates may explore entry-level technology roles or relevant postgraduate study pathways, subject to eligibility requirements."],
    ["Can I receive admission guidance?", "Yes. Prospective students may contact the University admissions team for programme, eligibility and application guidance."],
  ],
};

export default function CourseDetailPageLuxuryEditorial({
  course = sampleCourse,
  applyHref = "/apply-online",
  brochureHref = "/brochure.pdf",
}) {
  const [activeYear, setActiveYear] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);

  const selected = course.curriculum?.[activeYear] || course.curriculum?.[0];

  return (
    <main className="overflow-hidden bg-white text-[#132337]">
      {/* 01 — HERO: MAGAZINE COVER */}
      <section className="relative bg-[#071A2F] text-white">
        <div className="absolute inset-0 opacity-[0.06] [background-image:linear-gradient(rgba(255,255,255,.2)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.2)_1px,transparent_1px)] [background-size:80px_80px]" />

        <div className="relative mx-auto w-[min(1440px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
          <nav aria-label="Breadcrumb" className="flex flex-wrap gap-2 border-b border-white/10 py-6 text-sm text-white/50">
            <a href="/" className="hover:text-[#D5AD57]">Home</a><span>/</span>
            <a href="/programmes" className="hover:text-[#D5AD57]">Programmes</a><span>/</span>
            <span className="text-white">{course.shortTitle}</span>
          </nav>

          <div className="grid min-h-[760px] lg:grid-cols-[1fr_420px]">
            <div className="flex flex-col justify-between py-16 pr-0 lg:py-20 lg:pr-16">
              <div>
                <Eyebrow light>{course.school}</Eyebrow>
                <h1 className="mt-8 max-w-[980px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(52px,7vw,104px)] font-normal leading-[.9] tracking-[-0.055em]">
                  {course.title}
                </h1>
                <p className="mt-8 max-w-[760px] text-base leading-8 text-white/65 sm:text-lg">
                  {course.summary}
                </p>
              </div>

              <div className="mt-14 grid gap-8 border-t border-white/15 pt-8 sm:grid-cols-3">
                {[["Duration", course.duration], ["Level", course.level], ["Mode", course.mode]].map(([label, value]) => (
                  <div key={label}>
                    <span className="text-sm font-bold uppercase tracking-[0.12em] text-[#D5AD57]">{label}</span>
                    <strong className="mt-2 block text-lg font-semibold">{value}</strong>
                  </div>
                ))}
              </div>
            </div>

            <aside className="relative flex flex-col justify-end overflow-hidden border-x border-white/10 bg-[#0C2744]">
              {course.heroImage ? (
                <img src={course.heroImage} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
              ) : (
                <>
                  <div className="absolute -right-24 top-12 h-72 w-72 rounded-full border border-[#D5AD57]/30" />
                  <div className="absolute -right-10 top-28 h-52 w-52 rounded-full border border-[#D5AD57]/20" />
                  <div className="absolute left-10 top-28 font-['Libre_Caslon_Display',Georgia,serif] text-[150px] leading-none text-white/[0.04]">
                    {course.shortTitle}
                  </div>
                </>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#071A2F] via-transparent to-transparent" />
              <div className="relative p-7 sm:p-9">
                <span className="text-sm font-bold uppercase tracking-[0.13em] text-[#D5AD57]">Eligibility</span>
                <p className="mt-4 text-base leading-8 text-white/70">{course.eligibility}</p>
                <div className="mt-7 flex flex-col gap-3">
                  <a href={applyHref} className="inline-flex min-h-[54px] items-center justify-between rounded-full bg-[#8B1E3F] px-6 text-sm font-bold transition hover:bg-[#D5AD57] hover:text-[#071A2F]">
                    Apply for Admission <Arrow />
                  </a>
                  <a href={brochureHref} className="inline-flex min-h-[54px] items-center justify-between rounded-full border border-white/20 px-6 text-sm font-bold transition hover:border-[#D5AD57] hover:text-[#D5AD57]">
                    Download Brochure <Download />
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* 02 — OVERVIEW / MANIFESTO */}
      <section className="py-20 sm:py-24 lg:py-32">
        <div className="mx-auto grid w-[min(1340px,calc(100%-48px))] gap-12 max-sm:w-[calc(100%-28px)] lg:grid-cols-[.68fr_1.32fr]">
          <div>
            <Eyebrow>Programme Perspective</Eyebrow>
            <div className="mt-8 hidden h-36 w-px bg-[#071A2F]/15 lg:block" />
          </div>
          <div>
            <h2 className="font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,70px)] font-normal leading-[1.04] tracking-[-0.035em] text-[#071A2F]">
              An academic foundation for
              <span className="text-[#8B1E3F]"> meaningful participation in the digital world.</span>
            </h2>
            <p className="mt-8 max-w-4xl text-base leading-8 text-[#65717D]">{course.overview}</p>
          </div>
        </div>
      </section>

      {/* 03 — KNOWLEDGE PILLARS */}
      <section className="border-y border-[#071A2F]/10 bg-[#F7F2E8]">
        <div className="mx-auto grid w-[min(1440px,100%)] md:grid-cols-2 lg:grid-cols-4">
          {course.highlights?.map(([title, text], index) => (
            <article key={title} className="group min-h-[310px] border-b border-r border-[#071A2F]/10 p-7 sm:p-9">
              <div className="flex items-center justify-between">
                <span className="font-['Libre_Caslon_Display',Georgia,serif] text-5xl text-[#D5AD57]">0{index + 1}</span>
                <span className="h-10 w-10 rounded-full border border-[#071A2F]/15 transition group-hover:bg-[#8B1E3F]" />
              </div>
              <h3 className="mt-14 font-['Libre_Caslon_Display',Georgia,serif] text-3xl font-normal text-[#071A2F]">{title}</h3>
              <p className="mt-4 text-sm leading-7 text-[#65717D]">{text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* 04 — CURRICULUM: ACADEMIC INDEX */}
      <section className="py-20 sm:py-24 lg:py-32">
        <div className="mx-auto w-[min(1340px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
          <header className="grid gap-8 border-b-2 border-[#071A2F] pb-10 lg:grid-cols-[1fr_.55fr] lg:items-end">
            <div>
              <Eyebrow>Academic Structure</Eyebrow>
              <h2 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,68px)] font-normal leading-[1.03] text-[#071A2F]">
                Three stages of academic progression.
              </h2>
            </div>
            <p className="text-base leading-8 text-[#65717D]">
              A structured progression from foundations to applied learning and integrated project work.
            </p>
          </header>

          <div className="mt-10 grid gap-8 lg:grid-cols-[.8fr_1.2fr]">
            <div>
              {course.curriculum?.map((item, index) => {
                const active = index === activeYear;
                return (
                  <button
                    key={item.year}
                    type="button"
                    onClick={() => setActiveYear(index)}
                    className={cx(
                      "grid w-full grid-cols-[70px_1fr_42px] items-center border-b border-[#071A2F]/15 py-6 text-left transition",
                      active ? "text-[#8B1E3F]" : "text-[#071A2F] hover:text-[#8B1E3F]"
                    )}
                  >
                    <span className="text-sm font-bold">{item.year}</span>
                    <span className="font-['Libre_Caslon_Display',Georgia,serif] text-3xl font-normal">{item.label}</span>
                    <span className={cx("grid h-10 w-10 place-items-center rounded-full border", active ? "border-[#8B1E3F] bg-[#8B1E3F] text-white" : "border-[#071A2F]/15")}>
                      <Arrow className="h-4 w-4" />
                    </span>
                  </button>
                );
              })}
            </div>

            <article className="relative overflow-hidden bg-[#071A2F] p-7 text-white sm:p-10">
              <div className="absolute right-0 top-0 font-['Libre_Caslon_Display',Georgia,serif] text-[160px] leading-none text-white/[0.035]">
                0{activeYear + 1}
              </div>
              <div className="relative">
                <span className="text-sm font-bold uppercase tracking-[0.14em] text-[#D5AD57]">{selected?.year}</span>
                <h3 className="mt-4 font-['Libre_Caslon_Display',Georgia,serif] text-4xl font-normal sm:text-5xl">{selected?.label}</h3>
                <p className="mt-5 max-w-2xl text-base leading-8 text-white/60">{selected?.description}</p>
                <div className="mt-9 grid sm:grid-cols-2">
                  {selected?.subjects?.map((subject, index) => (
                    <div key={subject} className="flex min-h-[72px] items-center gap-4 border-b border-white/10 py-4 sm:odd:border-r sm:odd:pr-5 sm:even:pl-5">
                      <span className="text-sm font-bold text-[#D5AD57]">0{index + 1}</span>
                      <span className="text-sm font-semibold leading-6">{subject}</span>
                    </div>
                  ))}
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* 05 — LEARNING METHODOLOGY: EDITORIAL STEPS */}
      <section className="bg-[#F4E8EC] py-20 sm:py-24 lg:py-32">
        <div className="mx-auto w-[min(1340px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
          <div className="grid gap-12 lg:grid-cols-[.7fr_1.3fr]">
            <div>
              <Eyebrow>How You Learn</Eyebrow>
              <h2 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(40px,4.5vw,62px)] font-normal leading-[1.05] text-[#071A2F]">
                Academic depth meets practical application.
              </h2>
            </div>
            <div className="border-t-2 border-[#071A2F]">
              {course.methodology?.map(([title, text], index) => (
                <article key={title} className="grid gap-4 border-b border-[#071A2F]/15 py-7 sm:grid-cols-[70px_220px_1fr] sm:items-start">
                  <span className="text-sm font-bold text-[#A77B2D]">0{index + 1}</span>
                  <h3 className="font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal text-[#071A2F]">{title}</h3>
                  <p className="text-sm leading-7 text-[#65717D]">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 06 — OUTCOMES */}
      <section className="py-20 sm:py-24 lg:py-32">
        <div className="mx-auto grid w-[min(1340px,calc(100%-48px))] gap-12 max-sm:w-[calc(100%-28px)] lg:grid-cols-[.8fr_1.2fr]">
          <div className="relative min-h-[500px] overflow-hidden bg-[#0C2744] p-8 text-white sm:p-10">
            <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full border border-[#D5AD57]/20" />
            <div className="absolute -bottom-16 -right-16 h-72 w-72 rounded-full border border-[#D5AD57]/20" />
            <div className="relative flex h-full flex-col justify-between">
              <Eyebrow light>Graduate Profile</Eyebrow>
              <div className="mt-20">
                <span className="font-['Libre_Caslon_Display',Georgia,serif] text-[120px] leading-none text-[#D5AD57]">{course.shortTitle}</span>
                <p className="mt-6 max-w-md text-base leading-8 text-white/60">
                  A programme designed to help students develop academic, technical and professional capabilities for further progression.
                </p>
              </div>
            </div>
          </div>

          <div>
            <Eyebrow>Programme Outcomes</Eyebrow>
            <h2 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(40px,4.5vw,62px)] font-normal leading-[1.05] text-[#071A2F]">
              Capabilities developed through the learning journey.
            </h2>
            <div className="mt-9">
              {course.outcomes?.map((item, index) => (
                <div key={item} className="flex gap-5 border-b border-[#071A2F]/15 py-5">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#F7F2E8] text-sm font-bold text-[#8B1E3F]">0{index + 1}</span>
                  <p className="pt-1 text-base leading-8 text-[#65717D]">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 07 — CAREER CONSTELLATION */}
      <section className="bg-[#071A2F] py-20 text-white sm:py-24 lg:py-32">
        <div className="mx-auto w-[min(1340px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
          <header className="grid gap-8 lg:grid-cols-[1fr_.55fr] lg:items-end">
            <div>
              <Eyebrow light>Career Possibilities</Eyebrow>
              <h2 className="mt-6 max-w-4xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(42px,5vw,68px)] font-normal leading-[1.03]">
                Multiple directions. One strong academic foundation.
              </h2>
            </div>
            <p className="text-base leading-8 text-white/55">
              Career outcomes vary based on individual skills, experience, further study and employer requirements.
            </p>
          </header>

          <div className="mt-12 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {course.careers?.map((career, index) => (
              <article key={career} className="group min-h-[220px] bg-[#071A2F] p-6 transition hover:bg-[#0C2744]">
                <span className="text-sm font-bold text-[#D5AD57]">0{index + 1}</span>
                <h3 className="mt-16 font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal leading-8">{career}</h3>
                <Arrow className="mt-5 text-white/30 transition group-hover:text-[#D5AD57]" />
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 08 — ADMISSION ROADMAP */}
      <section className="bg-[#F7F2E8] py-20 sm:py-24 lg:py-32">
        <div className="mx-auto w-[min(1340px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
          <div className="grid gap-12 lg:grid-cols-[.65fr_1.35fr]">
            <div>
              <Eyebrow>Admissions</Eyebrow>
              <h2 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(40px,4.5vw,62px)] font-normal leading-[1.05] text-[#071A2F]">
                Your route to joining the programme.
              </h2>
              <a href={applyHref} className="mt-8 inline-flex min-h-[52px] items-center gap-3 rounded-full bg-[#8B1E3F] px-6 text-sm font-bold text-white transition hover:bg-[#071A2F]">
                Start Application <Arrow />
              </a>
            </div>

            <div className="relative border-l border-[#071A2F]/20 pl-7 sm:pl-10">
              {course.admissions?.map(([number, title, text], index) => (
                <article key={number} className={cx("relative pb-9", index === course.admissions.length - 1 && "pb-0")}>
                  <span className="absolute -left-[47px] top-0 grid h-8 w-8 place-items-center rounded-full bg-[#071A2F] text-xs font-bold text-[#D5AD57] sm:-left-[57px]">
                    {number}
                  </span>
                  <h3 className="font-['Libre_Caslon_Display',Georgia,serif] text-2xl font-normal text-[#071A2F]">{title}</h3>
                  <p className="mt-3 max-w-2xl text-sm leading-7 text-[#65717D]">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 09 — FAQ */}
      <section className="py-20 sm:py-24 lg:py-32">
        <div className="mx-auto w-[min(1160px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
          <header className="text-center">
            <Eyebrow>Programme FAQ</Eyebrow>
            <h2 className="mx-auto mt-6 max-w-3xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(40px,4.5vw,60px)] font-normal leading-[1.05] text-[#071A2F]">
              Questions prospective students often ask.
            </h2>
          </header>

          <div className="mt-12 border-t-2 border-[#071A2F]">
            {course.faq?.map(([question, answer], index) => {
              const open = openFaq === index;
              return (
                <article key={question} className="border-b border-[#071A2F]/15">
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => setOpenFaq(open ? -1 : index)}
                    className="grid min-h-[92px] w-full grid-cols-[48px_1fr_44px] items-center gap-4 py-5 text-left"
                  >
                    <span className="text-sm font-bold text-[#A77B2D]">0{index + 1}</span>
                    <span className="font-['Libre_Caslon_Display',Georgia,serif] text-xl font-normal leading-7 text-[#071A2F] sm:text-2xl">{question}</span>
                    <span className={cx("grid h-10 w-10 place-items-center rounded-full border transition", open ? "border-[#8B1E3F] bg-[#8B1E3F] text-white" : "border-[#071A2F]/15")}>
                      <Plus className={cx("transition duration-300", open && "rotate-45")} />
                    </span>
                  </button>
                  <div className={cx("grid transition-all duration-300", open ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}>
                    <div className="overflow-hidden">
                      <p className="mb-7 ml-16 max-w-3xl text-sm leading-8 text-[#65717D]">{answer}</p>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* 10 — FINAL ADMISSION STATEMENT */}
      <section className="relative overflow-hidden bg-[#8B1E3F] text-white">
        <div className="absolute -right-24 -top-32 h-[460px] w-[460px] rounded-full border border-white/10" />
        <div className="absolute -right-6 -top-20 h-[320px] w-[320px] rounded-full border border-white/10" />

        <div className="relative mx-auto grid w-[min(1340px,calc(100%-48px))] gap-8 py-16 max-sm:w-[calc(100%-28px)] lg:grid-cols-[1fr_auto] lg:items-center">
          <div>
            <span className="text-sm font-bold uppercase tracking-[0.15em] text-[#F2D99E]">Begin Your Journey</span>
            <h2 className="mt-4 max-w-4xl font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(36px,4vw,56px)] font-normal leading-[1.05]">
              Take the next step towards your university education.
            </h2>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={applyHref} className="inline-flex min-h-[54px] items-center gap-3 rounded-full bg-white px-7 text-sm font-bold text-[#8B1E3F] transition hover:bg-[#D5AD57] hover:text-[#071A2F]">
              Apply Now <Arrow />
            </a>
            <a href={brochureHref} className="inline-flex min-h-[54px] items-center gap-3 rounded-full border border-white/30 px-7 text-sm font-bold transition hover:border-[#D5AD57] hover:text-[#D5AD57]">
              <Download /> Brochure
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
